import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  Circle,
  ChevronLeft,
  ChevronRight,
  Award,
  BookOpen,
  Clock,
  Play
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { VimeoVideoPlayer } from '../components/VimeoVideoPlayer';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import {
  getCourseVideos,
  getVideoById,
  getNextVideo,
  getPreviousVideo,
  getTotalCourseDuration,
  courseModules,
  InstructionalVideo
} from '../config/instructionalVideos';

const CoursePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentVideoId, setCurrentVideoId] = useState<string>('mindset-shift');
  const [completedVideos, setCompletedVideos] = useState<Set<string>>(new Set());
  const [showCompletion, setShowCompletion] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const courseVideos = getCourseVideos();
  const currentVideo = getVideoById(currentVideoId);
  const nextVideo = getNextVideo(currentVideoId);
  const previousVideo = getPreviousVideo(currentVideoId);

  // Load user's progress
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const loadProgress = async () => {
      try {
        const { data, error } = await supabase
          .from('user_video_progress')
          .select('video_id, watched')
          .eq('user_id', user.id)
          .eq('watched', true);

        if (error) throw error;

        if (data) {
          const completed = new Set(data.map(v => v.video_id));
          setCompletedVideos(completed);
        }

        // Check if there's a video parameter in URL
        const videoParam = searchParams.get('video');
        if (videoParam && getVideoById(videoParam)) {
          setCurrentVideoId(videoParam);
        }

      } catch (error) {
        console.error('Error loading course progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [user, searchParams]);

  // Update URL when video changes
  useEffect(() => {
    setSearchParams({ video: currentVideoId }, { replace: true });
  }, [currentVideoId, setSearchParams]);

  const markVideoComplete = async () => {
    if (!user || !currentVideo) return;

    try {
      const { error } = await supabase
        .from('user_video_progress')
        .upsert({
          user_id: user.id,
          video_id: currentVideo.id,
          watched: true,
          completed_at: new Date().toISOString(),
          last_position: 0
        });

      if (error) throw error;

      setCompletedVideos(prev => new Set([...prev, currentVideo.id]));
      toast.success('Progress saved!');

      // Check if all videos are complete
      if (completedVideos.size + 1 === courseVideos.length) {
        setShowCompletion(true);
      }

    } catch (error) {
      console.error('Error marking video complete:', error);
      toast.error('Failed to save progress');
    }
  };

  const handleNextVideo = () => {
    if (nextVideo) {
      setCurrentVideoId(nextVideo.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousVideo = () => {
    if (previousVideo) {
      setCurrentVideoId(previousVideo.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const progressPercentage = Math.round((completedVideos.size / courseVideos.length) * 100);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!currentVideo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Video not found</p>
          <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {getTotalCourseDuration()} total
              </div>
              <div className="text-sm font-semibold text-blue-600">
                {completedVideos.size} of {courseVideos.length} completed
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Video Player */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Video Title */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium opacity-90">
                    Module {currentVideo.module} • Video {currentVideo.orderIndex} of {courseVideos.length}
                  </span>
                  {completedVideos.has(currentVideo.id) && (
                    <div className="flex items-center gap-1 text-green-300">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs font-medium">Completed</span>
                    </div>
                  )}
                </div>
                <h1 className="text-2xl font-bold">{currentVideo.title}</h1>
                <p className="text-blue-100 mt-2">{currentVideo.description}</p>
              </div>

              {/* Video Player */}
              <div className="p-6">
                <VimeoVideoPlayer
                  videoId={currentVideo.vimeoId}
                  title={currentVideo.title}
                  duration={currentVideo.duration}
                  showTitle={false}
                />
              </div>

              {/* Key Takeaways */}
              <div className="px-6 pb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-5">
                  <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Key Takeaways
                  </h3>
                  <ul className="space-y-2">
                    {currentVideo.keyTakeaways.map((takeaway, index) => (
                      <li key={index} className="flex items-start gap-3 text-sm text-green-800">
                        <span className="flex-shrink-0 w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-xs font-bold text-green-900">
                          {index + 1}
                        </span>
                        <span>{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Navigation */}
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={handlePreviousVideo}
                    disabled={!previousVideo}
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>

                  {!completedVideos.has(currentVideo.id) && (
                    <Button
                      onClick={markVideoComplete}
                      className="flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Mark Complete
                    </Button>
                  )}

                  <Button
                    onClick={handleNextVideo}
                    disabled={!nextVideo}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                  >
                    {nextVideo ? 'Next Video' : 'Course Complete!'}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Course Outline */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <h2 className="text-lg font-bold mb-4">Course Outline</h2>

              {courseModules.map((module) => (
                <div key={module.id} className="mb-6 last:mb-0">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Module {module.id}: {module.title}
                  </h3>
                  <p className="text-xs text-gray-600 mb-3">{module.description}</p>

                  <div className="space-y-2">
                    {module.videos.map((videoId) => {
                      const video = getVideoById(videoId);
                      if (!video) return null;

                      const isCompleted = completedVideos.has(videoId);
                      const isCurrent = currentVideoId === videoId;

                      return (
                        <button
                          key={videoId}
                          onClick={() => setCurrentVideoId(videoId)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                            isCurrent
                              ? 'bg-blue-100 border-2 border-blue-500'
                              : isCompleted
                              ? 'bg-green-50 hover:bg-green-100'
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-shrink-0 mt-0.5">
                              {isCompleted ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : isCurrent ? (
                                <Play className="w-4 h-4 text-blue-600" />
                              ) : (
                                <Circle className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium ${isCurrent ? 'text-blue-900' : 'text-gray-900'}`}>
                                {video.title}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {video.duration}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Course Completion Modal */}
      {showCompletion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Congratulations!
            </h2>
            <p className="text-gray-600 mb-6">
              You've completed the Career Foundation Course! You're now ready to create your BRAG and DREAM sheets with confidence.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/profile?tab=brag')}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Start Your BRAG Sheet →
              </Button>
              <Button
                onClick={() => setShowCompletion(false)}
                variant="outline"
                className="w-full"
              >
                Review Course
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursePage;
